import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Banknote, Printer, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { quoteImpound, recordImpoundPartPayment, deleteImpoundPartPayment, fetchBusinessInfo } from "../lib/api";
import { printHtmlViaIframe } from "../lib/printHtml";
import { tenantLocale, tenantTz } from "../lib/company";
import { hasCap } from "../lib/roles";
import { overstayDays } from "../lib/tenantTime";

const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" }) : "—";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "eftpos", label: "EFTPOS" },
  { value: "credit_card", label: "Credit Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "other", label: "Other" },
];

/**
 * Impound part-payment dialog.
 * Admin/dispatcher: record a payment (amount + method + note) → prints a
 * receipt for the billing user to cross-check. Viewer (billing role): read-only list with
 * re-print per payment. Balance = live quote total − payments to date.
 */
export default function PartPaymentDialog({ job, open, onOpenChange, role, onSaved }) {
  // `role` arrives as a prop here — wrap it so hasCap sees a user-shaped obj.
  const canRecord = hasCap({ role }, "admin", "dispatcher");
  const canVoid = hasCap({ role }, "admin");
  const [doc, setDoc] = useState(job);
  const [quote, setQuote] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("eftpos");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [voidingId, setVoidingId] = useState(null);
  const [printingId, setPrintingId] = useState(null);

  useEffect(() => {
    if (!open || !job) return;
    setDoc(job);
    setAmount("");
    setMethod("eftpos");
    setNote("");
    setQuote(null);
    let alive = true;
    (async () => {
      try {
        const overstay = overstayDays(job.impound_release_due_at);
        const q = await quoteImpound({
          gvm_kg: Number(job.gvm_kg || 0),
          impounded_at: job.impounded_at,
          term: job.impound_term || "28d",
          base_to_base_km: job.route_distance_m ? job.route_distance_m / 1000 : null,
          days_overstay: overstay,
          is_public_holiday: job.is_public_holiday || null,
        });
        if (alive) setQuote(q);
      } catch {
        /* fall back to the saved breakdown */
      }
    })();
    return () => { alive = false; };
  }, [open, job]);

  if (!job) return null;

  const payments = doc?.part_payments || [];
  const total = quote?.total ?? doc?.pricing_breakdown?.total ?? null;
  const paid = payments.reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const balance = total != null ? Math.max(0, +(total - paid).toFixed(2)) : null;

  const printReceipt = async (payment, paymentsList = null) => {
    setPrintingId(payment.id);
    try {
      let biz = {};
      try { biz = await fetchBusinessInfo(); } catch { /* letterhead optional */ }
      // Use the explicit list when provided — right after recording, React
      // state hasn't re-rendered yet and the closure's `payments` is stale
      // (the new payment isn't in it → "paid to date" wrongly computed $0
      // and the receipt showed the FULL balance).
      const plist = paymentsList || payments;
      const idx = plist.findIndex((p) => p.id === payment.id);
      const upto = idx >= 0 ? plist.slice(0, idx + 1) : plist;
      const paidToDate = upto.reduce((a, p) => a + (Number(p.amount) || 0), 0);
      const balAfter = total != null ? Math.max(0, +(total - paidToDate).toFixed(2)) : null;
      const trade = (biz.trading_name || "the towing company").replace(/ Ltd$/i, "");
      const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Part Payment Receipt — ${payment.receipt_no}</title>
<style>
  @page { size: A4; margin: 18mm 20mm; }
  html, body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 26px; }
  .trading { font-size: 18pt; font-weight: bold; }
  .lh { text-align: right; line-height: 1.4; font-size: 10pt; }
  h1 { font-size: 15pt; letter-spacing: 1px; margin: 0 0 4px; }
  .sub { color: #333; font-size: 10pt; margin-bottom: 18px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 10.5pt; }
  th, td { border: 1px solid #555; padding: 7px 10px; text-align: left; }
  th { background: #f1f5f9; width: 32%; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .right { text-align: right; font-family: 'Courier New', monospace; }
  .big { font-size: 13pt; font-weight: bold; }
  .foot { margin-top: 22px; font-size: 9.5pt; color: #333; border-top: 1px solid #999; padding-top: 10px; }
  .sig { margin-top: 26px; display: flex; justify-content: space-between; font-size: 10pt; }
</style></head>
<body>
<div class="top">
  <div>
    <h1>PART PAYMENT RECEIPT</h1>
    <div class="sub">Police Impound — payment towards release charges</div>
  </div>
  <div class="lh">
    <div class="trading">${biz.trading_name || "the towing company"}</div>
    <div>${biz.trading_as || ""}</div>
    <div>${biz.address_line1 || ""}</div>
    <div>${biz.address_line2 || ""}</div>
    <div>${biz.phone_primary || ""}</div>
  </div>
</div>
<table>
  <tr><th>Receipt #</th><td class="right">${payment.receipt_no}</td><th>Date Received</th><td>${fmtDateTime(payment.received_at)}</td></tr>
  <tr><th>Registration</th><td class="right">${doc.rego || "—"}</td><th>Vehicle</th><td>${doc.vehicle || "—"}</td></tr>
  <tr><th>Driver / Owner</th><td colspan="3">${doc.customer_name || doc.impound_owner_name || "—"}</td></tr>
</table>
<table>
  <tr><th>Amount Received</th><td class="right big">${fmtMoney(payment.amount)}</td></tr>
  <tr><th>Payment Method</th><td>${(PAYMENT_METHODS.find((m) => m.value === payment.method) || {}).label || payment.method}</td></tr>
  <tr><th>Received By</th><td>${payment.received_by || "—"}</td></tr>
  ${payment.note ? `<tr><th>Note</th><td>${payment.note}</td></tr>` : ""}
</table>
<table>
  <tr><th>Total impound charges${quote ? " (incl. overstay to date)" : ""}</th><td class="right">${total != null ? fmtMoney(total) : "—"}</td></tr>
  <tr><th>Total paid to date (incl. this payment)</th><td class="right">${fmtMoney(paidToDate)}</td></tr>
  <tr><th>Balance remaining</th><td class="right big">${balAfter != null ? fmtMoney(balAfter) : "—"}</td></tr>
</table>
<div class="foot">
  The vehicle will not be released until all charges are paid in full. Storage fees continue to accrue after the
  release-due date until the vehicle is collected, so the balance shown may increase over time. Please retain this
  receipt — it will be credited against the final amount at release. ${trade}.
</div>
<div class="sig">
  <span>Customer signature: ____________________</span>
  <span>Date: ____________</span>
</div>
</body></html>`;
      printHtmlViaIframe(html, "__part_payment_print_frame__");
    } finally {
      setPrintingId(null);
    }
  };

  const onRecord = async () => {
    const val = Number(amount);
    if (!Number.isFinite(val) || val <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    setSaving(true);
    try {
      const updated = await recordImpoundPartPayment(job.id, { amount: val, method, note });
      setDoc(updated);
      setAmount("");
      setNote("");
      onSaved?.(updated);
      toast.success("Part payment recorded — printing receipt");
      const latest = (updated.part_payments || [])[updated.part_payments.length - 1];
      if (latest) setTimeout(() => printReceipt(latest, updated.part_payments), 200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  const onVoid = async (payment) => {
    setVoidingId(payment.id);
    try {
      const updated = await deleteImpoundPartPayment(job.id, payment.id);
      setDoc(updated);
      onSaved?.(updated);
      toast.success(`Payment ${payment.receipt_no} voided`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to void payment");
    } finally {
      setVoidingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="part-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black tracking-tight flex items-center gap-2">
            <Banknote className="h-5 w-5 text-emerald-600" />
            Part Payments
          </DialogTitle>
          <DialogDescription>
            {job.rego || "—"} · {job.vehicle} · {job.impound_owner_name || job.customer_name}
          </DialogDescription>
        </DialogHeader>

        {/* Account summary */}
        <div className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-3" data-testid="part-payment-summary">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Charges to date</div>
              <div className="font-mono font-bold text-slate-900 dark:text-slate-100">{total != null ? fmtMoney(total) : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Paid so far</div>
              <div className="font-mono font-bold text-emerald-700 dark:text-emerald-300" data-testid="part-payment-paid">{fmtMoney(paid)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Balance</div>
              <div className="font-mono font-black text-lg text-slate-900 dark:text-slate-100" data-testid="part-payment-balance">{balance != null ? fmtMoney(balance) : "—"}</div>
            </div>
          </div>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 italic mt-2 text-center">
            Charges include overstay accrued to today — balance keeps growing until collection.
          </p>
        </div>

        {/* Payment history */}
        <div className="space-y-1.5" data-testid="part-payment-list">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
            Payments received ({payments.length})
          </div>
          {payments.length === 0 && (
            <p className="text-xs text-slate-400 italic">No part payments recorded yet.</p>
          )}
          {payments.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-2 rounded border border-slate-200 dark:border-slate-700 px-2.5 py-1.5"
              data-testid={`part-payment-row-${p.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
                  {fmtMoney(p.amount)} · {(PAYMENT_METHODS.find((m) => m.value === p.method) || {}).label || p.method}
                  <span className="ml-1.5 font-mono text-[10px] text-slate-400">{p.receipt_no}</span>
                </div>
                <div className="text-[10px] text-slate-500 truncate">
                  {fmtDateTime(p.received_at)} · {p.received_by}{p.note ? ` · ${p.note}` : ""}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8 px-2.5 text-xs shrink-0"
                onClick={() => printReceipt(p)}
                disabled={printingId === p.id}
                data-testid={`part-payment-print-${p.id}`}
                title="Print receipt for this payment"
              >
                {printingId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Printer className="h-3 w-3" />}
              </Button>
              {canVoid && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-2.5 text-xs shrink-0 border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950/30"
                  onClick={() => onVoid(p)}
                  disabled={voidingId === p.id}
                  data-testid={`part-payment-void-${p.id}`}
                  title="Void this payment (admin only)"
                >
                  {voidingId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Record a new payment — admin / dispatcher only */}
        {canRecord && (
          <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
              Record new part payment
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="uppercase-label">Amount *</Label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
                  <Input
                    type="number" step="0.01" min="0" className="pl-5 font-mono"
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder={balance != null ? String(balance) : "0.00"}
                    data-testid="part-payment-amount"
                  />
                </div>
              </div>
              <div>
                <Label className="uppercase-label">Method *</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="part-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="uppercase-label">Note (optional)</Label>
                <Textarea
                  rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. weekly payment plan instalment 2 of 4"
                  data-testid="part-payment-note"
                />
              </div>
            </div>
            <Button
              onClick={onRecord}
              disabled={saving}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
              data-testid="part-payment-record-btn"
            >
              {saving ? <Loader2 className="h-4 w-4.5 animate-spin" /> : <Printer className="h-4 w-4.5" />}
              Record Payment &amp; Print Receipt
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="part-payment-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
